import type React from 'react';
import type { ReactEventHandler } from 'react';
import {
  useCallback, useEffect, useMemo, useState,
} from 'react';

import { useGetImage } from '@vlabs/api/luna-api/client';
import type { LunaClient } from '@vlabs/api-bindings';
import { apiContainer } from '@vlabs/api-bindings';
import { BboxRenderer } from '@vlabs/monokit/shared/ui/canvas';
import type { TOriginSize } from '@vlabs/monokit/shared/ui/canvas';
import { useLocation } from 'react-router-dom';

import st from './ImageChecksProcessingImage.module.sass';
import { useChecksResultContext } from '../checksResultContext';
import { BBOX_STYLES } from './constants';

interface ImageChecksProcessingImageProps {
  file: File;
}

function ImageChecksProcessingImage({
  file,
}: ImageChecksProcessingImageProps): React.JSX.Element | null {
  const { search } = useLocation();
  const imageId = new URLSearchParams(search).get('imageId');
  const { data: serverImage } = useGetImage(imageId ?? '', undefined, { query: { enabled: !!imageId } });
  const displayImage = imageId ? serverImage : file;
  const [originSize, setOriginSize] = useState<TOriginSize | undefined>(undefined);
  const [src, setSrc] = useState<string>();
  const { checkResult, bboxList, setBboxList } = useChecksResultContext();

  useEffect(() => {
    setOriginSize(undefined);
    if (!displayImage) {
      setSrc(undefined);
      return undefined;
    }
    const objectUrl = URL.createObjectURL(displayImage);
    setSrc(objectUrl);
    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [displayImage]);

  useEffect(() => {
    (apiContainer.lunaClient as LunaClient).sdk.estimate(file, {
      detect_face: 1,
    }).then((resp) => {
      setBboxList?.(
        resp.data?.images_estimations[0]?.estimations
          .map((estimation) => estimation.face.detection.rect),
      );
    });
  }, [file, setBboxList]);

  const coloredBboxList = useMemo(() => bboxList?.map((bbox) => ({
    ...bbox,
    style: bbox.style ?? BBOX_STYLES[String(checkResult)],
  })), [bboxList, checkResult]);

  const loadImage: ReactEventHandler<HTMLImageElement> = useCallback((e) => {
    const { currentTarget: image } = e;
    if (!image) return;
    setOriginSize({
      width: image.naturalWidth,
      height: image.naturalHeight,
    });
  }, []);

  if (!src) return null;
  return (
    <div className={st.Root}>
      <div className={st.Inner}>
        <img
          alt="background"
          className={st.Background}
          src={src}
        />
        <img
          alt="object to check"
          className={st.Image}
          onLoad={loadImage}
          src={src}
          style={{
            backgroundImage: src,
          }}
        />
        <BboxRenderer
          bboxList={coloredBboxList}
          originSize={originSize}
        />
      </div>
    </div>
  );
}

export { ImageChecksProcessingImage };
